'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { ChevronDown, Search, X } from 'lucide-react'

export type SearchableMultiSelectOption = {
  id: string | number
  code?: string
  label: string
}

export function SearchableMultiSelect({
  options,
  selectedIds,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search by code or name…',
  loading = false,
  disabled = false,
  theme = 'light',
  className = '',
}: {
  options: SearchableMultiSelectOption[]
  selectedIds: (string | number)[]
  onChange: (ids: (string | number)[]) => void
  placeholder?: string
  searchPlaceholder?: string
  loading?: boolean
  disabled?: boolean
  theme?: 'dark' | 'light'
  className?: string
}) {
  const isDark = theme === 'dark'
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<Map<number, HTMLLabelElement>>(new Map())

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.label.toLowerCase().includes(q) || (o.code ?? '').toLowerCase().includes(q))
  }, [options, query])

  // Keep the highlighted row in range whenever the visible (filtered) list changes.
  useEffect(() => {
    setActiveIndex((i) => (filtered.length === 0 ? -1 : Math.min(Math.max(i, 0), filtered.length - 1)))
  }, [filtered])

  useEffect(() => {
    if (activeIndex < 0) return
    rowRefs.current.get(activeIndex)?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedOptions = useMemo(() => options.filter((o) => selectedSet.has(o.id)), [options, selectedSet])

  const toggle = (id: string | number) => {
    onChange(selectedSet.has(id) ? selectedIds.filter((existing) => existing !== id) : [...selectedIds, id])
  }

  // Acts on the filtered list: with a search query, selects/unselects only
  // the matches; selections outside the filter are kept.
  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => selectedSet.has(o.id))
  const toggleAll = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filtered.map((o) => o.id))
      onChange(selectedIds.filter((id) => !filteredIds.has(id)))
    } else {
      const merged = new Set(selectedIds)
      for (const o of filtered) merged.add(o.id)
      onChange([...merged])
    }
  }

  const onSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const active = filtered[activeIndex]
      if (active) toggle(active.id)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const remove = (id: string | number, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(selectedIds.filter((existing) => existing !== id))
  }

  const triggerClasses = isDark
    ? 'bg-slate-900 border-slate-800 text-slate-100 hover:border-slate-700'
    : 'bg-white border-slate-200 text-slate-800 hover:border-slate-300'
  const panelClasses = isDark ? 'bg-slate-950 border-slate-800' : 'bg-white border-slate-200'
  const dividerClasses = isDark ? 'border-slate-800' : 'border-slate-200'
  const mutedText = isDark ? 'text-slate-500' : 'text-slate-400'
  const rowHover = isDark ? 'hover:bg-slate-900' : 'hover:bg-slate-50'
  const chipClasses = isDark ? 'bg-slate-800 text-slate-100' : 'bg-slate-100 text-slate-700'
  const inputText = isDark ? 'text-slate-100 placeholder:text-slate-600' : 'text-slate-800 placeholder:text-slate-400'

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((v) => !v)}
        className={`flex min-h-9 w-full flex-wrap items-center gap-1 rounded border px-2 py-1.5 text-left text-[13px] transition-colors disabled:opacity-50 ${triggerClasses}`}
      >
        {selectedOptions.length === 0 && <span className={mutedText}>{loading ? 'Loading…' : placeholder}</span>}
        {selectedOptions.map((o) => (
          <span key={o.id} className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] ${chipClasses}`}>
            {o.label}
            <X className="h-3 w-3 cursor-pointer opacity-70 hover:opacity-100" onClick={(e) => remove(o.id, e)} />
          </span>
        ))}
        <ChevronDown className={`ml-auto h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${mutedText}`} />
      </button>

      {open && (
        <div className={`absolute z-20 mt-1 w-full rounded border shadow-lg ${panelClasses}`}>
          <div className={`flex items-center gap-2 border-b px-2 py-1.5 ${dividerClasses}`}>
            <Search className={`h-3.5 w-3.5 shrink-0 ${mutedText}`} />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder={searchPlaceholder}
              className={`w-full border-0 bg-transparent text-[13px] outline-none ${inputText}`}
            />
          </div>
          {filtered.length > 0 && (
            <div className={`border-b px-3 py-1.5 ${dividerClasses}`}>
              <button
                type="button"
                onClick={toggleAll}
                className={`text-[12px] font-medium hover:underline ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
              >
                {allFilteredSelected ? 'Unselect all' : 'Select all'}
                {query.trim() && ` (${filtered.length})`}
              </button>
            </div>
          )}
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 && <div className={`px-3 py-2 text-[12px] ${mutedText}`}>No results</div>}
            {filtered.map((o, idx) => {
              const checked = selectedSet.has(o.id)
              const active = idx === activeIndex
              return (
                <label
                  key={o.id}
                  ref={(el) => { if (el) rowRefs.current.set(idx, el); else rowRefs.current.delete(idx) }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] ${active ? (isDark ? 'bg-slate-900' : 'bg-slate-50') : ''} ${rowHover}`}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggle(o.id)} className="shrink-0" />
                  <span className="flex-1 truncate">{o.label}</span>
                  {o.code && <span className={`shrink-0 truncate font-mono text-[10px] ${mutedText}`}>{o.code}</span>}
                </label>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
